/**
 * AuthLayout — minimal chrome for the login + signup screens.
 *
 * Two-column split: branded gradient panel on the left, form area on the right.
 * On screens narrower than 900 px the panel collapses and only the form shows.
 */
import { Outlet } from "react-router-dom";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { BrandMark } from "@/components/app/brand-mark";
import { useUiAutoAnimate } from "@/components/ui/auto-animate";

export function AuthLayout() {
  const animatedContentRef = useUiAutoAnimate();
  const currentYear = new Date().getFullYear();

  return (
    <div className="grid h-dvh w-full lg:grid-cols-[minmax(380px,520px)_1fr]">
      {/* Brand panel — left side. Hidden below lg breakpoint. */}
      <aside className="relative hidden overflow-hidden bg-aurora-grad bg-[length:220%_220%] [animation:auroraShift_9s_ease-in-out_infinite] p-10 text-white lg:flex lg:flex-col lg:justify-between">
        <style>{`@keyframes auroraShift { 0% { background-position: 0% 30%; } 25% { background-position: 100% 70%; } 50% { background-position: 20% 100%; } 75% { background-position: 90% 20%; } 100% { background-position: 0% 30%; } }`}</style>
        {/* Soft ambient blobs add depth — pure CSS, no images. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-32 -right-32 h-96 w-96 animate-pulse rounded-full bg-white/15 blur-3xl"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute bottom-[-8rem] left-[-6rem] h-[28rem] w-[28rem] animate-pulse rounded-full bg-cyan/30 blur-3xl [animation-duration:2s]"
        />

        <div className="relative flex items-center gap-3">
          <BrandMark size={36} />
          <span className="text-xl font-extrabold tracking-tight">Opus</span>
        </div>

        <div className="relative max-w-[360px]">
          <h1 className="m-0 text-4xl font-extrabold leading-tight tracking-tight">
            AI Operations Hub
          </h1>
          <p className="mt-3 text-base leading-relaxed text-white/85">
            AI-powered auditing, monitoring, and provisioning for every vendor
            GuestTek operates.
          </p>
        </div>

        <div className="relative flex items-center justify-between text-xs text-white/60">
          <span>© {currentYear} GuestTek · Opus v2.0</span>
          <span className="font-mono">network automation</span>
        </div>
      </aside>

      {/* Form area — right side. Theme toggle in the corner so users on the
          login screen can still switch modes before signing in. */}
      <main className="relative flex items-center justify-center overflow-y-auto px-6 py-10">
        <div className="absolute right-5 top-5">
          <ThemeToggle />
        </div>
        <div ref={animatedContentRef} className="w-full max-w-md">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
